import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, API, errMsg } from "@/lib/api";
import { Panel, PageHead, Stat, DataTable } from "@/crm/ui";

const ENTITIES = [
  { key: "employees", label: "Employees", required: "employee_name" },
  { key: "partners", label: "Partners", required: "partner_name" },
  { key: "banks", label: "Banks", required: "bank_name" },
  { key: "products", label: "Products", required: "product_name" },
  { key: "commission-rules", label: "Commission Rules", required: "user_type, commission_type" },
  { key: "leads", label: "Leads", required: "customer_name" },
];

export default function ImportExport() {
  const [entity, setEntity] = useState("employees");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  const spec = ENTITIES.find((e) => e.key === entity);

  const send = async (commit) => {
    if (!file) return toast.error("Choose a CSV or XLSX file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/crm/import/${entity}?commit=${commit}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(commit ? `${data.committed} record(s) imported` : `Validated ${data.total} row(s)`);
      if (commit) { setFile(null); if (inputRef.current) inputRef.current.value = ""; }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-export-page">
      <PageHead eyebrow="Administration" title="Bulk import & export"
                subtitle="Two-phase import: validate first, review the totals, then commit. Nothing is written to the database until you confirm. Every import and export is audited." />

      <Panel title="Export master data" testid="export-panel">
        <div className="flex flex-wrap gap-2">
          {ENTITIES.map((e) => (
            <Button key={e.key} variant="outline" className="bg-white" data-testid={`export-${e.key}-btn`}
                    onClick={() => window.open(`${API}/crm/export/${e.key}`, "_blank")}>
              <Download className="mr-2 h-4 w-4" /> {e.label}
            </Button>))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Sensitive columns (PAN, GST, bank account, IFSC) are masked unless you have the
          view-sensitive-data permission.
        </p>
      </Panel>

      <Panel title="Import" testid="import-panel"
             aside={<span className="text-xs text-muted-foreground">Required: {spec.required} · max 2000 rows</span>}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Entity</Label>
            <select value={entity} data-testid="import-entity-select"
                    onChange={(e) => { setEntity(e.target.value); setResult(null); }}
                    className="h-9 rounded-md border border-input bg-white px-2 text-sm">
              {ENTITIES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">CSV / XLSX file</Label>
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" data-testid="import-file-input"
                   onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
                   className="block text-xs" />
          </div>
          <Button variant="outline" className="bg-white" disabled={busy} onClick={() => send(false)}
                  data-testid="validate-import-btn">
            <Upload className="mr-2 h-4 w-4" /> Validate
          </Button>
          <Button disabled={busy || !result || !result.ready_to_import} onClick={() => send(true)}
                  data-testid="commit-import-btn">
            Import {result?.ready_to_import ? `${result.ready_to_import} row(s)` : ""}
          </Button>
        </div>
      </Panel>

      {result && (
        <>
          <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-5">
            <Stat label="Total rows" value={result.total} testid="import-total" />
            <Stat label="Valid" value={result.valid} testid="import-valid" />
            <Stat label="Invalid" value={result.invalid} testid="import-invalid" />
            <Stat label="Duplicate" value={result.duplicate} testid="import-duplicate" />
            <Stat label="Imported" value={result.committed} testid="import-committed" />
          </div>
          <Panel title="Preview (first 10 valid rows)" testid="import-preview-panel">
            <DataTable testid="import-preview-table" rows={result.preview}
                       columns={(result.preview[0] ? Object.keys(result.preview[0]) : ["info"])
                         .slice(0, 12).map((k) => ({ key: k, label: k.replace(/_/g, " ") }))} />
          </Panel>
          {!!result.invalid_rows?.length && (
            <Panel title={`Invalid rows (${result.invalid})`} testid="import-invalid-panel">
              <DataTable testid="import-invalid-table"
                         rows={result.invalid_rows.map((r) => ({ row: r.row, errors: r.errors.join("; ") }))}
                         columns={[{ key: "row", label: "File Row" }, { key: "errors", label: "Problems" }]} />
            </Panel>)}
          {!!result.duplicate_rows?.length && (
            <Panel title={`Duplicate rows (${result.duplicate})`} testid="import-duplicate-panel">
              <DataTable testid="import-duplicate-table"
                         rows={result.duplicate_rows.map((r) => ({ row: r.row, on: r.duplicate_on.join(", ") }))}
                         columns={[{ key: "row", label: "File Row" }, { key: "on", label: "Duplicate On" }]} />
            </Panel>)}
        </>
      )}
    </div>
  );
}
